import { formatarCep, formatarCpfCnpj, formatarData, formatarMoeda, formatarNumero, formatarTelefone, type ColunaGrade } from '@persona/ui';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import type { CampoUi } from '../dicionario/tipos';

/** Consultas cujo código é técnico (IBGE): na lista e no resumo basta o nome ("Curitiba", não "4106902 · Curitiba"). */
const CONSULTAS_SO_NOME = new Set(['CAD_MUNICIPIOS', 'CAD_PAISES']);

function descricaoConsulta(c: CampoUi, v: unknown, descricoes?: Map<unknown, string>) {
  const texto = descricoes?.get(v);
  if (texto === undefined) return `#${String(v)}`;
  if (!c.consulta || !CONSULTAS_SO_NOME.has(c.consulta.codigo)) return texto;
  const separador = texto.indexOf(' · ');
  return separador < 0 ? texto : texto.slice(separador + 3);
}

/** Exibição de um valor pelo tipo do dicionário (07 §3.2): números à direita, datas dd/mm/aaaa, lógico como ✓, lista pelo rótulo. */
export function exibir(c: CampoUi, v: unknown, descricoes?: Map<unknown, string>): ReactNode {
  if (v === null || v === undefined || v === '') return '';
  switch (c.tipo) {
    case 'consulta': return descricaoConsulta(c, v, descricoes);
    case 'telefone': return formatarTelefone(String(v));
    case 'cep': return formatarCep(String(v));
    case 'moeda': return formatarMoeda(v as number);
    case 'numero': case 'quantidade': case 'preco': case 'percentual': return formatarNumero(v as number, c.decimais) + (c.tipo === 'percentual' ? ' %' : '');
    case 'data': return formatarData(String(v));
    case 'dataHora': return formatarData(String(v), true);
    case 'logico': return v ? <Check size={15} aria-label="Sim" /> : <span className="sr-only">Não</span>;
    case 'lista': return c.opcoes?.find((o) => o.valor === v)?.rotulo ?? String(v);
    case 'cpfCnpj': return formatarCpfCnpj(String(v));
    case 'senha': return '••••••';
    default: return String(v);
  }
}

const NUMERICOS = new Set(['numero', 'moeda', 'quantidade', 'preco', 'percentual']);

export function colunaDe<T extends Record<string, unknown>>(c: CampoUi, descricoes?: Map<unknown, string>): ColunaGrade<T> {
  const numerico = NUMERICOS.has(c.tipo);
  const largura = c.tipo === 'logico' ? '72px' : c.tipo === 'consulta' ? 'minmax(140px, 1.2fr)' : c.tipo === 'telefone' ? 'minmax(128px, 0.8fr)' : numerico ? 'minmax(110px, 0.8fr)'
    : c.tamanho <= 8 ? 'minmax(96px, 0.6fr)' : c.tamanho <= 30 ? (c.mono ? 'minmax(168px, 1fr)' : 'minmax(160px, 1fr)') : 'minmax(220px, 2fr)';
  return {
    campo: c.nome,
    titulo: c.tituloCurto ?? c.titulo,
    largura,
    alinhar: numerico ? 'direita' : 'esquerda',
    mono: c.mono,
    formatar: (v) => exibir(c, v, descricoes),
    negativo: numerico ? (v) => typeof v === 'number' && v < 0 : undefined,
  };
}

/** 'nome.asc,uf.desc' (URL, 07 §2.1) ⇄ lista de ordem ⇄ 'nome,-uf' (servidor, 01 §6.1). */
export function lerOrdem(o: string | undefined): { campo: string; direcao: 'asc' | 'desc' }[] {
  if (!o) return [];
  return o.split(',').map((p) => {
    const [campo, d] = p.split('.');
    return { campo, direcao: d === 'desc' ? 'desc' as const : 'asc' as const };
  }).filter((x) => x.campo);
}

export const escreverOrdem = (l: { campo: string; direcao: 'asc' | 'desc' }[]) => l.map((x) => `${x.campo}.${x.direcao}`).join(',') || undefined;
export const ordemServidor = (l: { campo: string; direcao: 'asc' | 'desc' }[]) => l.map((x) => (x.direcao === 'desc' ? `-${x.campo}` : x.campo)).join(',');
